import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Plus, Eye, Send, CheckCircle, Clock, XCircle, DollarSign, ChevronRight, Trash2 } from 'lucide-react';
import SortableHeader, { useTableSort, useColumnFilters, usePagination, TablePagination } from '../components/table/SortableHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_STYLES = {
    draft: 'bg-stone-50 text-stone-600 border-stone-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    viewed: 'bg-amber-50 text-amber-700 border-amber-200',
    accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
};

export default function Proposals() {
    const { getAuthHeader } = useAuth();
    const { can } = usePermissions();
    const navigate = useNavigate();
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const res = await axios.get(`${API}/proposals`, { headers: getAuthHeader() });
            setProposals(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch {} finally { setLoading(false); }
    };

    const handleDelete = async (p) => {
        if (!window.confirm(`Delete "${p.title}"?`)) return;
        try {
            await axios.delete(`${API}/proposals/${p.proposal_id}`, { headers: getAuthHeader() });
            setProposals(prev => prev.filter(x => x.proposal_id !== p.proposal_id));
            toast.success('Proposal deleted');
        } catch { toast.error('Failed to delete'); }
    };

    const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : v ? `$${v.toFixed(2)}` : '$0';
    const stats = { total: proposals.length, sent: proposals.filter(p => p.status === 'sent').length, accepted: proposals.filter(p => p.status === 'accepted').length, value: proposals.reduce((s, p) => s + (p.total || 0), 0) };

    const PROPOSAL_COLS = [
        { key: 'title', label: 'Proposal' },
        { key: 'account_name', label: 'Company' },
        { key: 'total', label: 'Total' },
        { key: 'valid_until', label: 'Valid Until' },
        { key: 'status', label: 'Status' },
    ];
    const { columnFilters, setColumnFilter, filteredData: filteredProposals } = useColumnFilters(proposals);
    const { sortColumn, sortDirection, handleSort, sortedData: sortedProposals } = useTableSort(filteredProposals, { numericKeys: ['total'] });
    const { page, pageSize, setPage, setPageSize, pageData: pagedProposals, totalPages, total: totalProposals } = usePagination(sortedProposals, 25);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Total Proposals', value: stats.total, color: 'text-stone-700' },
                    { label: 'Sent', value: stats.sent, color: 'text-blue-600' },
                    { label: 'Accepted', value: stats.accepted, color: 'text-emerald-600' },
                    { label: 'Total Value', value: formatVal(stats.value), color: 'text-violet-600' },
                ].map((m, i) => (
                    <div key={i} className="bg-white rounded-xl border border-stone-200 p-5 card-hover">
                        <p className={`text-2xl font-bold ${m.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                        <p className="text-xs text-stone-500 mt-0.5">{m.label}</p>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between">
                <p className="text-sm text-stone-500">{proposals.length} proposals</p>
                <button onClick={() => navigate('/proposals/new')} data-testid="new-proposal-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 shadow-sm">
                    <Plus className="w-4 h-4" /> Create Proposal
                </button>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="proposals-table">
                {loading ? (
                    <div className="p-12 text-center text-stone-400">Loading...</div>
                ) : proposals.length === 0 ? (
                    <div className="p-12 text-center">
                        <FileText className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500 text-sm mb-3">No proposals yet</p>
                        <button onClick={() => navigate('/proposals/new')} className="text-stone-700 text-sm">Create your first proposal</button>
                    </div>
                ) : (
                    <>
                    <table className="w-full">
                        <thead>
                            <tr className="bg-stone-50 border-b border-stone-200">
                                {PROPOSAL_COLS.map(col => (
                                    <SortableHeader
                                        key={col.key}
                                        label={col.label}
                                        columnKey={col.key}
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection}
                                        onSort={handleSort}
                                        filterValue={columnFilters[col.key]}
                                        onFilter={setColumnFilter}
                                        allData={proposals}
                                    />
                                ))}
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {pagedProposals.map(p => (
                                <tr key={p.proposal_id} className="hover:bg-stone-50 cursor-pointer group" onClick={() => navigate(`/proposals/${p.proposal_id}`)} data-testid={`proposal-row-${p.proposal_id}`}>
                                    <td className="px-4 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center">
                                                <FileText className="w-4 h-4 text-stone-600" />
                                            </div>
                                            <p className="text-sm font-medium text-stone-900">{p.title}</p>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-stone-600">{p.account_name || '—'}</td>
                                    <td className="px-4 py-4 text-sm font-semibold text-stone-900">{formatVal(p.total)}</td>
                                    <td className="px-4 py-4 text-sm text-stone-500">{p.valid_until || '—'}</td>
                                    <td className="px-4 py-4">
                                        <span className={`text-xs font-medium px-2 py-1 rounded-full border capitalize ${STATUS_STYLES[p.status] || 'bg-stone-50 text-stone-600 border-stone-200'}`}>{p.status}</span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {can('proposals:delete') && <button onClick={e => { e.stopPropagation(); handleDelete(p); }}
                                                className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors" data-testid={`delete-proposal-${p.proposal_id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>}
                                            <ChevronRight className="w-4 h-4 text-stone-300" />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <TablePagination
                        page={page}
                        totalPages={totalPages}
                        total={totalProposals}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                    />
                    </>
                )}
            </div>
        </div>
    );
}
